import { useEffect, useState } from "react";
import api from "../utils/api";
import {
  UilEstate,
  UilBox,
  UilWebGrid,
  UilUtensils,
  UilShoppingCart,
  UilChart,
  UilComment,
  UilShoppingBag,
  UilUsersAlt,
  UilSetting,
  UilInvoice
} from "@iconscout/react-unicons";



export const SIDEBAR_CONFIG = [
  {
    id: "dashboard",
    icon: UilEstate,
    heading: "Dashboard",
    link: "/dashboard",
    roles: ["admin", "superAdmin"],
    visible: true,
  },
  {
    id: "product-management",
    icon: UilBox,
    heading: "Product Management",
    roles: ["admin", "superAdmin"],
    visible: true,
    submenu: [
      { id: "product-products", heading: "Products", link: "/product", visible: true },
      { id: "product-category", heading: "Category", link: "/categories", visible: true },
      { id: "product-subcategory", heading: "Subcategory", link: "/subcategories", visible: true },
      { id: "product-combos", heading: "Combos", link: "/combos", visible: true },
      { id: "product-update", heading: "Update", link: "/price-update", visible: true },
      {
        id: "product-status",
        heading: "Product Status",
        link: "/reports",
        roles: ["superAdmin"],
        visible: true,
      },
    ],
  },
  {
    id: "banner",
    icon: UilWebGrid,
    heading: "Banner",
    link: "/banner",
    roles: ["admin", "superAdmin"],
    visible: true,
  },
  {
    id: "other-apps-sale",
    icon: UilInvoice,
    heading: "Other Apps Sale",
    link: "/other-apps-sale",
    roles: ["admin", "superAdmin"],
    visible: true,
  },

  {
    id: "table-bookings",
    icon: UilUtensils,
    heading: "Table Bookings",
    link: "/table-booking",
    roles: ["waiter", "admin", "superAdmin"],
    visible: (features) => features?.tableBooking !== false,
  },
  {
    id: "online-orders",
    icon: UilShoppingCart,
    heading: "Online Orders",
    link: "/orderdetails",
    roles: ["admin", "superAdmin"],
    visible: (features) => features?.onlineOrders !== false,
  },
  {
    id: "orders-report",
    icon: UilChart,
    heading: "Report",
    roles: ["admin", "superAdmin"],
    visible: true,
    submenu: [
      { id: "orders-history", heading: "Order Report", link: "/orders-history", visible: true },
      { id: "customer-report", heading: "Customer Report", link: "/customer-report", visible: true },
      { id: "product-report", heading: "Product Report", link: "/product-report", visible: true },
      { id: "other-apps-sale-report", heading: "Summary", link: "/report-other-app-sale", visible: true },
    ],
  },
  {
    id: "user-feedback",
    icon: UilComment,
    heading: "User Feedback",
    link: "/feedback",
    roles: ["admin", "superAdmin"],
    visible: (features) => features?.feedback !== false,
  },
  {
    id: "takeaway",
    icon: UilShoppingBag,
    heading: "Takeaway",
    link: "/takeaway",
    roles: ["admin", "superAdmin"],
    visible: (features) => features?.takeaway !== false,
  },
  {
    id: "user-management",
    icon: UilUsersAlt,
    heading: "User Management",
    link: "/addusers",
    roles: ["admin", "superAdmin"],
    visible: true,
  },
  {
    id: "settings",
    icon: UilSetting,
    heading: "Settings",
    link: "/setting",
    roles: ["superAdmin"],
    visible: true,
  },
  {
    id: "configure",
    icon: UilSetting,
    heading: "Configure",
    link: "/configure",
    roles: ["fingliderAdmin"],
    visible: true,
  },
];


export const getSidebarByRole = (role, features = {}, sidebarPermissions = null) => {
  const hasAccess = (item) => {
    // If dynamic permissions exist for this item's id, use those
    const allowedRoles = sidebarPermissions && sidebarPermissions[item.id] !== undefined
      ? sidebarPermissions[item.id] 
      : item.roles;

    const roleCheck =
      item.visible !== false &&
      (!allowedRoles || allowedRoles.includes(role));

    const featureCheck =
      typeof item.visible === "function"
        ? item.visible(features)
        : true;

    return roleCheck && featureCheck;
  };

  return SIDEBAR_CONFIG
    .filter((item) => hasAccess(item))
    .map((item) => ({
      ...item,
      submenu: item.submenu
        ? item.submenu.filter((sub) => hasAccess(sub))
        : undefined,
    }))
    .filter((item) => !item.submenu || item.submenu.length > 0);
};


export const useDashboardData = () => {
  const [cardsData, setCardsData] = useState([
    {
      title: "Today's Sales",
      icon: "shopping",
      color: "bg-blue-200 border-blue-400",
      link: "/orders-history",
      series: [{ name: "Sales", data: [0] }],
    },
    {
      title: "Total Orders",
      icon: "shopping",
      color: "bg-emerald-200 border-emerald-400",
      link: "/orders-history",
      series: [{ name: "Total", data: [0] }],
    },
    {
      title: "Weekly Orders",
      icon: "calendar",
      color: "bg-violet-200 border-violet-400",
      link: "/orders-history",
      series: [{ name: "Weekly", data: [0] }],
    },
    {
      title: "Monthly Orders",
      icon: "calendar",
      color: "bg-rose-200 border-rose-400",
      link: "/orders-history",
      series: [{ name: "Monthly", data: [0] }],
    },
    {
      title: "Customer Rating",
      icon: "check",
      color: "bg-amber-200 border-amber-400",
      link: "/feedback",
      series: [{ name: "Rating", data: [0] }],
    },
    {
      title: "Total Products",
      icon: "box",
      color: "bg-cyan-200 border-cyan-400",
      link: "/product",
      series: [{ name: "Products", data: [0] }],
    },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/orders/order-stats");

        const {
          totalOrders = 0,
          todayOrders = 0,
          weeklyOrders = 0,
          monthlyOrders = 0,
          todaySales = 0,
          totalProducts = 0,
          customerRating = 0,
        } = response.data;

        setCardsData((prevData) =>
          prevData.map((card, index) => {
            let value = 0;

            switch (index) {
              case 0:
                value = todaySales;
                break;
              case 1:
                value = totalOrders;
                break;
              case 2:
                value = weeklyOrders;
                break;
              case 3:
                value = monthlyOrders;
                break;
              case 4:
                value = customerRating;
                break;
              case 5:
                value = totalProducts;
                break;
              default:
                value = 0;
            }

            return {
              ...card,
              series: [{ name: card.title, data: [value] }],
            };
          })
        );
      } catch (error) {
        console.error("Error fetching order data:", error);
      }
    };

    fetchData();
  }, []);

  return cardsData;
};
